﻿import {PlayerPrefs} from "../../Utils/Unity/PlayerPrefs";
import {UIGameObject} from "../Tools/Base/UIGameObject";
import {UIButton} from "../Tools/Base/UIButton";
import {UIText} from "../Tools/Base/UIText";
import {Time} from "../../Utils/Unity/Time";
import {GameCategory, GameScreen, MoneyType, SlotItem} from "../../enums";
import {Core} from "../../Base/Core";
import {Random} from "../../Utils/Unity/Random";
import {UISprite} from "../Tools/Base/UISprite";
import {gsap} from "gsap";
import {SlotMachine} from "../../Slots/SlotMachine";
import {SpinsOfferButton} from "../SpinsOfferButton";
import {RewardedAdsBtn} from "../RewardedAdsBtn";

const PREFS_PLAYED = "eruicty8n574t6yvn34yxt23";

export class UISlots extends UISprite {
    get isPlayerPlayedSlots() {
        return PlayerPrefs.HasKey(PREFS_PLAYED);
    }

    set isPlayerPlayedSlots(value) {
        PlayerPrefs.SetInt(PREFS_PLAYED, ~~!!value);
    }

    get spinBtnIn() {
        return this.machine.spinBtn.pressed;
    }

    set spinBtnIn(val) {
        this.machine.spinBtn.pressed = !!val;
    }

    Awake() {
        super.Awake();

        this.onPlayerChanged = this.onPlayerChanged.bind(this);
        this.onStarted = this.onStarted.bind(this);
        this.onFinished = this.onFinished.bind(this);

        //--------------------------------------
        this.back = this.addChild(new UISprite("slots/slotBG"));
        this.back.interactive = true; // to prevent click-through

        this.shine = this.back.addChild(new UISprite("slots/shine"));
        this.machine = this.back.addChild(new SlotMachine());
        this.tip = this.addChild(
            new UIText(
                Core.Localizator.Get("autospin_tip", {
                    fill: 0x000000,
                    dropShadow: false,
                })
            )
        );
        this.adsBtn = this.addChild(
            new RewardedAdsBtn("btn-green", {
                label: Core.Localizator.Get("ads_spins_number", Core.GameData.Slots.restoreSpinsNumberByAds),
                icon: "video",
            })
        );
        //--------------------------------------

        this.closeBtn = this.addChild(new UIButton("button-close"));
        this.offerBtn = this.addChild(new SpinsOfferButton());
        this.spinAttention = this.addChild(new UIGameObject());
        this.vfxLayer = this.addChild(new PIXI.Container);

        this.isCombo = false;
        this.comboItem = null; // SlotItem
        this.noComboItems = []; // SlotItem[3]
        this.finished = false;
        this.finishTime = 0;
        this.finishTimeout = 2;

        this._shineAnimation = null;

        this.machine.on("Started", this.onStarted);
        this.machine.on("Finished", this.onFinished);

        this.closeBtn.on("pointertap", () => this.onClickClose());
        this.adsBtn.on("pointertap", () => {
            Core.Ads.TriggerRewarded(
                () => {
                    Core.Player.AddMoney(MoneyType.Spins, Core.GameData.Slots.restoreSpinsNumberByAds);
                    Core.Player.Timers.RestoreSpinsByAdsDay = Core.Time.DayUniqId;
                }
            );
        });

        Core.Player.on("Changed", this.onPlayerChanged);

        this.SetActive(false);
    }

    onClickClose() {
        this.Hide();
        if (this.finished && Time.time - this.finishTime > this.finishTimeout) {
            this.Hide();
        }
    }

    animateShine() {
        if (!this._shineAnimation) {
            this._shineAnimation = gsap
                .timeline({repeat: -1, repeatDelay: 0, yoyo: true})
                .set(this.shine, {alpha: 0})
                .to(this.shine, {alpha: 1, duration: 2});
        }
        this._shineAnimation.play(0);
    }

    stopShine() {
        if (this._shineAnimation) {
            this._shineAnimation.pause(0);
        }
    }

    Show() {
        Core.Player.PlanetsSave.TutorSave.spins = true;
        this.finished = true;
        this.onPlayerChanged();
        this.adsBtn.label = Core.Localizator.Get("ads_spins_number", Core.GameData.Slots.restoreSpinsNumberByAds);
        this.spinAttention.SetActive(!this.isPlayerPlayedSlots);
        this.fitLayout();
        this.machine.Show();
        this.animateShine();
        super.Show();
        this._checkTutorial();
    }

    Hide() {
        if (this.Active) {
            Core.Ads.TriggerInterstitial();
        }
        this.machine.Hide();
        this.stopShine();
        super.Hide();
    }

    fitLayout(viewport = Core.app.viewport) {
        let rect = viewport.clone();

        let scale = rect.height / this.back.texture.height;
        rect.width = this.back.texture.width * scale;
        rect.x = (viewport.width - rect.width) * 0.5;
        rect.y = (viewport.height - rect.height) * 0.5;

        this.back.fitRect(viewport, true);
        let backRect = this.back.getLocalBounds();

        this.shine.fitRect(backRect);
        this.machine.fitLayout(backRect);

        this.closeBtn.fitRect(new PIXI.Rectangle(viewport.width * 0.85, viewport.width * 0.2, viewport.width * 0.1, viewport.width * 0.1));

        this.offerBtn.fitLayout(
            new PIXI.Rectangle(viewport.width * 0.85, viewport.width * 0.35, viewport.width * 0.1, viewport.width * 0.1)
        );

        this.adsBtn.fitLayout(
            new PIXI.Rectangle(viewport.width * 0.84, viewport.height - viewport.width * 0.11, viewport.width * 0.15, viewport.width * 0.1)
        );

        this.tip.fitLayout(
            new PIXI.Rectangle(viewport.width * 0.35, viewport.height - viewport.width * 0.15, viewport.width * 0.3, viewport.width * 0.05)
        );

        this._checkTutorial();
    }

    _checkTutorial() {
        if (Core.Player.PlanetsSave.TutorSave.step !== 7) return;
        if (!this.Active) return;
        Core.UI.tutorial.ShowObjectHand(this.machine.spinBtn);
    }

    onStarted() {
        this.closeBtn.interactive = false;
        this.closeBtn.alpha = 0.5;
    }

    onFinished() {
        this.finishTime = Time.time;
        this.finished = true;
        let slotItem = this.isCombo
            ? Core.GameData.Slots.GetComboRewardForItem(this.comboItem)
            : Core.GameData.Slots.GetNoComboRewardForItems(this.noComboItems);
        this.machine.showResult(slotItem, this.isCombo, 4);
        if (slotItem) {
            this.finishTimeout = 2;
            slotItem.reward.Take(this.isCombo ? "slots_combo" : "slots_nocombo");
            this.showRewardVFX(slotItem);
        } else {
            this.finishTimeout = 0;
        }
        this.closeBtn.interactive = true;
        this.closeBtn.alpha = 1;
    }

    showRewardVFX(slotItem) {
        switch (slotItem.itemType) {
            case SlotItem.MoneySoft1:
                return Core.VFX.slotCoins(this.isCombo ? 50 : 20);
            case SlotItem.MoneySoft2:
                return Core.VFX.slotCoins(this.isCombo ? 100 : 40);
            case SlotItem.Creature1:
            case SlotItem.Creature2:
                let bonusCreature = Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(slotItem.reward.valueInt);
                return Core.VFX.slotCreature(bonusCreature);
            case SlotItem.MoneyHard:
                return Core.VFX.slotDiamonds();
            case SlotItem.Spin:
                return Core.VFX.slotSpins(this.machine);
        }
    }

    onPlayerChanged() {
        this.adsBtn.SetActive(Core.Player.SpinsNumber == 0);
    }

    _checkEnoughMoneyForSpin() {
        if (Core.Player.EnoughMoney(MoneyType.Spins, 1)) return true;
        if (Core.Player.EnoughMoney(Core.GameData.Slots.purchaseSpinsPrice)) return true;
        return false;
    }

    Update() {
        this.offerBtn.Update();
        this.machine.Update();
        if (this.spinBtnIn && this.finished) {
            if (this._checkEnoughMoneyForSpin()) {
                if (Time.time - this.finishTime > 0.75) {
                    this.spin();
                }
            } else {
                this.spinBtnIn = false;
                Core.UI.notEnoughHardMoney.Show();
            }
        }
    }

    spin() {
        Core.Player.PlanetsSave.TutorSave.spin = true;
        Core.UI.tutorial.HideObjectHand(this.machine.spinBtn);

        if (!Core.Player.SpendMoney(MoneyType.Spins, 1, GameCategory.slots, GameScreen.slots, "spin")) {
            if (!Core.Player.SpendMoney(Core.GameData.Slots.purchaseSpinsPrice, GameCategory.empty, GameScreen.empty, "slots")) {
                return Core.UI.notEnoughHardMoney.Show();
            }
        }

        Core.Player.PlanetsSave.TutorSave.spin = true;
        Core.Player.SpinsUsed++;
        this.isPlayerPlayedSlots = true;
        this.spinAttention.SetActive(false);
        this.finished = false;

        let mainRandom = Core.GameData.Slots.GetMainRandom(Core.Player.SpinsNumber);
        this.isCombo = !!mainRandom.HasCombo;
        this.comboItem = this.isCombo ? mainRandom.itemType : null;
        this.noComboItems = [0, 0, 0];

        if (!this.isCombo) {
            if (mainRandom.Teasering) {
                this.noComboItems[0] = this.noComboItems[1] = Core.GameData.Slots.GetTeaseringItem(Core.Player.SpinsNumber).itemType;
                this.noComboItems[2] = Core.GameData.Slots.GetNoComboRandomExcept(Core.Player.SpinsNumber, this.noComboItems[0]).itemType;
            } else {
                for (let i = 0; i < this.noComboItems.length; i++) {
                    this.noComboItems[i] = Core.GameData.Slots.GetNoComboRandom(Core.Player.SpinsNumber).itemType;
                }
                let hasCombo = this.noComboItems[0] == this.noComboItems[1] && this.noComboItems[0] == this.noComboItems[2];
                if (hasCombo) {
                    let availableItems = [SlotItem.Creature1, SlotItem.Creature2, SlotItem.MoneyHard, SlotItem.Spin].filter(
                        (type) => type !== this.noComboItems[0]
                    );
                    this.noComboItems[2] = availableItems[Random.Range(0, availableItems.length)];
                }
            }
        }

        let teasering = !this.isCombo && mainRandom.Teasering;
        let items = this.isCombo ? new Array(3).fill(this.comboItem) : this.noComboItems;
        this.machine.Spin(items, teasering);
    }
}
